import { computed, Ref } from '@vue/composition-api';
import store             from '@/store';
import RedrawService     from '@/services/RedrawService';
import MapService        from '@/services/MapService';

export interface IUseOptions {
  selectLocationMode: Ref<boolean>,
  parkingsLayer: Ref<boolean>,
  parkingZonesLayer: Ref<boolean>,
  parkingMetersLayer: Ref<boolean>,
  parkingSpotsLayer: Ref<boolean>,
  parkingGroupsLayer: Ref<boolean>,
  eventsLayer: Ref<boolean>,
  eventsAccidentsLayer: Ref<boolean>,
  eventsBuildingLayer: Ref<boolean>,
  eventsBlocksLayer: Ref<boolean>,
  eventsHazardLayer: Ref<boolean>,
  weatherStationsLayer: Ref<boolean>,
  handleLocationModeChange: () => void,
  handleParkingsChange: (value: boolean) => void,
  handleParkingZonesChange: (value: boolean) => void,
  handleParkingMetersChange: (value: boolean) => void,
  handleParkingSpotsChange: (value: boolean) => void,
  handleParkingGroupsChange: (value: boolean) => void,
  handleEventsChange: (value: boolean) => void,
  handleEventsAccidentsChange: (value: boolean) => void,
  handleEventsBuildingLayerChange: (value: boolean) => void,
  handleEventsBlocksLayerChange: (value: boolean) => void,
  handleEventsHazardChange: (value: boolean) => void,
  handleWeatherStationsChange: (value: boolean) => void,
}

const useOptions = (): IUseOptions => {
  const selectLocationMode: Ref<boolean> =
    computed(() => store.state.modes.selectLocation);
  const parkingsLayer: Ref<boolean> =
    computed(() => store.state.mapSettings.mapConfiguration.parkings);
  const parkingZonesLayer: Ref<boolean> =
    computed(() => store.state.mapSettings.mapConfiguration.parkingZone);
  const parkingMetersLayer: Ref<boolean> =
    computed(() => store.state.mapSettings.mapConfiguration.parkingMeters);
  const parkingSpotsLayer: Ref<boolean> =
    computed(() => store.state.mapSettings.mapConfiguration.parkingSpots);
  const parkingGroupsLayer: Ref<boolean> =
    computed(() => store.state.mapSettings.mapConfiguration.parkingGroups);
  const eventsLayer: Ref<boolean> =
    computed(() => store.state.mapSettings.mapConfiguration.events);
  const eventsAccidentsLayer: Ref<boolean> =
    computed(() => store.state.mapSettings.mapConfiguration.accident);
  const eventsBuildingLayer: Ref<boolean> =
    computed(() => store.state.mapSettings.mapConfiguration.constructions);
  const eventsBlocksLayer: Ref<boolean> =
    computed(() => store.state.mapSettings.mapConfiguration.roadClosed);
  const eventsHazardLayer: Ref<boolean> =
    computed(() => store.state.mapSettings.mapConfiguration.hazard);
  const weatherStationsLayer: Ref<boolean> =
    computed(() => store.state.mapSettings.mapConfiguration.weatherStations);
  const handleLocationModeChange = () => {
    store.commit('flipLocationMode');
  };
  const redrawMap = (): void => {
    const redrawService: RedrawService =
      new RedrawService(new MapService(store.state.leafletMapInstance));
    redrawService.redrawMap();
  };
  const toggleParentlayers = (parentLayerName: string): void => {
    switch (parentLayerName) {
      case 'eventsLayer':
        (store.state.mapSettings.mapConfiguration.accident
        || store.state.mapSettings.mapConfiguration.constructions
        || store.state.mapSettings.mapConfiguration.roadClosed
        || store.state.mapSettings.mapConfiguration.hazard) === true
          ? store.commit('enableEventsLayer')
          : store.commit('disableEventsLayer');
        break;
      case 'parkingsLayer':
        (store.state.mapSettings.mapConfiguration.parkingGroups
          || store.state.mapSettings.mapConfiguration.parkingSpots
        ) === true
          ? store.commit('enableParkingsLayer')
          : store.commit('disableParkingsLayer');
        break;
      default:
        break;
    }
  };
  const toggleSubLayers = (parentLayerName: string): void => {
    switch (parentLayerName) {
      case 'eventsLayer':
        if (store.state.mapSettings.mapConfiguration.events === true) {
          store.commit('enableEventsAccidentsLayer');
          store.commit('enableEventsBuildingLayer');
          store.commit('enableEventsBlocksLayer');
          store.commit('enableEventsHazardLayer');
        } else {
          store.commit('disableEventsAccidentsLayer');
          store.commit('disableEventsBuildingLayer');
          store.commit('disableEventsBlocksLayer');
          store.commit('disableEventsHazardLayer');
        }
        break;
      case 'parkingsLayer':
        if (store.state.mapSettings.mapConfiguration.parkings === true) {
          store.commit('enableParkingGroupsLayer');
          store.commit('enableParkingSpotsLayer');
        } else {
          store.commit('disableParkingGroupsLayer');
          store.commit('disableParkingSpotsLayer');
        }
        break;
      default:
        break;
    }
  };
  // parkings (grupa)
  const handleParkingsChange = (value: boolean) => {
    if (value) {
      store.commit('enableParkingsLayer');
    } else {
      store.commit('disableParkingsLayer');
    }
    toggleSubLayers('parkingsLayer');
    redrawMap();
  };
  // parkingZone
  const handleParkingZonesChange = (value: boolean) => {
    if (value) {
      store.commit('enableParkingZonesLayer');
    } else {
      store.commit('disableParkingZonesLayer');
    }
    redrawMap();
  };
  // parkingMeters
  const handleParkingMetersChange = (value: boolean) => {
    if (value) {
      store.commit('enableParkingMetersLayer');
    } else {
      store.commit('disableParkingMetersLayer');
    }
    redrawMap();
  };
  // parkingSpots
  const handleParkingSpotsChange = (value: boolean) => {
    if (value) {
      store.commit('enableParkingSpotsLayer');
    } else {
      store.commit('disableParkingSpotsLayer');
    }
    toggleParentlayers('parkingsLayer');
    redrawMap();
  };
  // parkingGroups
  const handleParkingGroupsChange = (value: boolean) => {
    if (value) {
      store.commit('enableParkingGroupsLayer');
    } else {
      store.commit('disableParkingGroupsLayer');
    }
    toggleParentlayers('parkingsLayer');
    redrawMap();
  };
  // events (grupa)
  const handleEventsChange = (value: boolean) => {
    if (value) {
      store.commit('enableEventsLayer');
    } else {
      store.commit('disableEventsLayer');
    }
    toggleSubLayers('eventsLayer');
    redrawMap();
  };
  // accident
  const handleEventsAccidentsChange = (value: boolean) => {
    if (value) {
      store.commit('enableEventsAccidentsLayer');
    } else {
      store.commit('disableEventsAccidentsLayer');
    }
    toggleParentlayers('eventsLayer');
    redrawMap();
  };
  // construction
  const handleEventsBuildingLayerChange = (value: boolean) => {
    if (value) {
      store.commit('enableEventsBuildingLayer');
    } else {
      store.commit('disableEventsBuildingLayer');
    }
    toggleParentlayers('eventsLayer');
    redrawMap();
  };
  // roadClosed
  const handleEventsBlocksLayerChange = (value: boolean) => {
    if (value) {
      store.commit('enableEventsBlocksLayer');
    } else {
      store.commit('disableEventsBlocksLayer');
    }
    toggleParentlayers('eventsLayer');
    redrawMap();
  };
  // hazard
  const handleEventsHazardChange = (value: boolean) => {
    if (value) {
      store.commit('enableEventsHazardLayer');
    } else {
      store.commit('disableEventsHazardLayer');
    }
    toggleParentlayers('eventsLayer');
    redrawMap();
  };
  // weatherStations
  const handleWeatherStationsChange = (value: boolean) => {
    if (value) {
      store.commit('enableWeatherStationsLayer');
    } else {
      store.commit('disableWeatherStationsLayer');
    }
    redrawMap();
  };
  return {
    selectLocationMode,
    parkingsLayer,
    parkingZonesLayer,
    parkingMetersLayer,
    parkingSpotsLayer,
    parkingGroupsLayer,
    eventsLayer,
    eventsAccidentsLayer,
    eventsBuildingLayer,
    eventsBlocksLayer,
    eventsHazardLayer,
    weatherStationsLayer,
    handleLocationModeChange,
    handleParkingsChange,
    handleParkingZonesChange,
    handleParkingMetersChange,
    handleParkingSpotsChange,
    handleParkingGroupsChange,
    handleEventsChange,
    handleEventsAccidentsChange,
    handleEventsBuildingLayerChange,
    handleEventsBlocksLayerChange,
    handleEventsHazardChange,
    handleWeatherStationsChange,
  };
};

export default useOptions;
